import { create } from 'zustand';

import { loadWidget, Variant } from '@asurion-core/widget-client/lib/package';

import {
  INITIAL_WIDGET_PARAMS,
  readWidgetParamsFromHash,
  writeWidgetParamsToHash,
  type WidgetParams,
} from './widgetParamsHash';

export type { WidgetParams };

type LoadedWidget = Awaited<ReturnType<typeof loadWidget<Variant>>>;

export type WidgetState = {
  value: LoadedWidget | undefined;
  loading: boolean;
  error: Error | undefined;
};

type PlaygroundStore = {
  widgetParams: WidgetParams;
  editorValue: string;
  widget: WidgetState;
  setWidgetParams: (value: WidgetParams) => void;
  setEditorValue: (value: string) => void;
};

const formatParams = (params: WidgetParams): string => JSON.stringify(params, null, '  ');

export const usePlaygroundStore = create<PlaygroundStore>((set) => {
  const initial = readWidgetParamsFromHash();
  return {
    widgetParams: initial,
    editorValue: formatParams(initial),
    widget: { value: undefined, loading: false, error: undefined },
    setWidgetParams: (widgetParams) => {
      set({ widgetParams, editorValue: formatParams(widgetParams) });
      writeWidgetParamsToHash(widgetParams);
    },
    setEditorValue: (editorValue) => set({ editorValue }),
  };
});

if (typeof window !== 'undefined') {
  const loadForParams = async (params: WidgetParams): Promise<void> => {
    if (!params.id) {
      usePlaygroundStore.setState({ widget: { value: undefined, loading: false, error: undefined } });
      return;
    }
    usePlaygroundStore.setState({ widget: { value: undefined, loading: true, error: undefined } });
    try {
      const value = await loadWidget({ loaderUrl: process.env.NX_PUBLIC_WIDGET_LOADER_URL, ...params });
      // Reference-equality check: bail if params changed while awaiting.
      if (usePlaygroundStore.getState().widgetParams !== params) return;
      usePlaygroundStore.setState({ widget: { value, loading: false, error: undefined } });
    } catch (error) {
      if (usePlaygroundStore.getState().widgetParams !== params) return;
      usePlaygroundStore.setState({
        widget: {
          value: undefined,
          loading: false,
          error: error instanceof Error ? error : new Error(String(error)),
        },
      });
    }
  };

  usePlaygroundStore.subscribe((state, prev) => {
    if (state.widgetParams !== prev.widgetParams) void loadForParams(state.widgetParams);
  });

  window.addEventListener('hashchange', () => {
    const widgetParams = readWidgetParamsFromHash();
    usePlaygroundStore.setState({ widgetParams, editorValue: formatParams(widgetParams) });
  });

  void loadForParams(usePlaygroundStore.getState().widgetParams);
}

export { INITIAL_WIDGET_PARAMS };
